/* How the contract is going.
 *
 * The screen leads with the one number University Health holds us to, against
 * the figure they hold us to, because that is the question every other number
 * here is context for. The definitions are on the page rather than behind a
 * link: a rate whose basis is a click away is a rate somebody will quote
 * without reading the basis.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { Loading } from '../../app/Loading';
import { Pager, usePaged } from '../../app/Pager';
import { Section } from '../../app/Section';
import { useAuth, useProjectTimezone } from '../../app/auth';
import { todayIn } from '../../lib/when';
import type { Site } from './Sites';

interface Totals {
    orders: number; delivered: number; notDelivered: number; cancelled: number;
    stillOpen: number; attempts: number; onTimeMet: number; onTimeMissed: number; notMeasured: number;
}
interface Rates {
    completionRate: number | null; onTimeRate: number | null;
    dryRunRate: number | null; literalScopeRatio: number | null;
}
interface Slice { key: string; label: string; totals: Totals; rates: Rates }

interface Report {
    from: string; to: string; grouping: string; timezone: string; generatedAt: string;
    totals: Totals; rates: Rates;
    target: { completion: number; internalGoal: number };
    meetsContract: boolean | null;
    byPeriod: Slice[]; byServiceType: Slice[]; bySite: Slice[]; byZone: Slice[]; byDayType: Slice[];
    /* Optional: a cached bundle can outlive the server that grew these, and a
       report page that throws is worse than one missing a card. */
    turnaround?: {
        inOurHands: { count: number; medianMinutes: number | null; p90Minutes: number | null };
        endToEnd: { count: number; medianMinutes: number | null; p90Minutes: number | null };
    };
    failureReasons?: Array<{ code: string; label: string; packages: number; orders: number }>;
    definitions: Array<{ measure: string; definition: string; note: string }>;
}

/** Minutes as something a person reads. 95 is an hour and thirty-five. */
const minutes = (n: number | null) => {
    if (n === null) return 'n/a';
    if (n < 60) return `${n} min`;
    const h = Math.floor(n / 60);
    const m = n % 60;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
};

const pct = (value: number | null) => (value === null ? 'n/a' : `${value.toFixed(1)}%`);

const GROUPINGS = [
    { value: 'day', label: 'Day' }, { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' }, { value: 'quarter', label: 'Quarter' },
];

/** The last N days, as service dates in the project's zone.
 *
 * Not UTC. From 7pm in Chicago onwards `toISOString()` is already tomorrow,
 * so the range this page opened on ended on a day that had not happened and
 * the header said so. Service dates are questions about San Antonio. */
function defaultRange(days: number, timezone: string): { from: string; to: string } {
    const now = Date.now();
    return {
        from: todayIn(timezone, new Date(now - (days - 1) * 86400000)),
        to: todayIn(timezone),
    };
}

export function Reports() {
    const { code = '' } = useParams();
    const { projects } = useAuth();
    const project = projects.find((p) => p.code === code);
    const [params, setParams] = useSearchParams();
    const [report, setReport] = useState<Report | null>(null);
    /* The glossary too, which 5.15 left whole as "a reference rather than a
       list of records". Ten measures is two pages, so the argument against
       was never strong; the count line says there are ten either way, and
       a quality team reading one definition is not reading all ten. */
    const pagedDefs = usePaged(report?.definitions ?? []);
    const [msg, setMsg] = useState<string | null>(null);

    const fallback = defaultRange(30, useProjectTimezone(code));
    const from = params.get('from') ?? fallback.from;
    const to = params.get('to') ?? fallback.to;
    const groupBy = params.get('groupBy') ?? 'week';
    /* ── WHICH PHARMACY THE CONTRACT FIGURES ARE ABOUT ───────────────────
     *
     * The page read as one number for the whole contract. That is the right
     * headline and the wrong only answer: nine counters release work on
     * nine different rhythms, and a completion rate that dips is a question
     * about one of them, not about all nine. "By pharmacy" further down
     * showed the split but could not be made the basis of the headline, the
     * rates, or the export.
     *
     * THE SERVER ALREADY TOOK `siteId` -- `gather` has filtered on it since
     * the reports router was written. Only the screen never offered it, so
     * this is a control over an existing filter rather than a new one, and
     * no query had to be touched to add it.
     *
     * EMPTY MEANS THE WHOLE CONTRACT, and stays the default. What we are
     * measured on is the contract; a page that opened on one pharmacy would
     * quietly answer a different question from the one it is named after. */
    const siteId = params.get('siteId') ?? '';
    const [sites, setSites] = useState<Site[]>([]);
    /* ONE STRING, USED THREE TIMES: the report request, the reload, and the
       workbook link. The export inheriting the filter is the point -- a
       spreadsheet that silently covered nine counters when the screen above
       it showed one is the kind of mismatch that reaches a client meeting. */
    const query = `from=${from}&to=${to}&groupBy=${groupBy}${siteId === '' ? '' : `&siteId=${siteId}`}`;

    useEffect(() => {
        /* Silent on failure: without the list the filter offers nothing and
           the report still builds, which is the behaviour it had before. */
        void api<Site[]>(`/api/projects/${code}/uh/sites`).then(setSites).catch(() => setSites([]));
    }, [code]);

    const load = useCallback(async () => {
        setMsg(null);
        try {
            setReport(await api<Report>(`/api/projects/${code}/uh/reports/sla?${query}`));
        } catch (err) {
            setMsg(err instanceof ApiError ? err.message : 'Could not build the report.');
        }
    }, [code, query]);
    useEffect(() => { void load(); }, [load]);

    if (!project) return (<><h1>Not available</h1><Link className="izy-btn secondary" to="/">Back</Link></>);
    if (report === null) {
        return msg
            ? (<><h1>Performance</h1><div className="izy-alert error" role="alert">{msg}</div></>)
            : <div className="izy-card"><Loading label="Building the report" /></div>;
    }

    const set = (key: string, value: string) => {
        const next = new URLSearchParams(params);
        next.set('from', from);
        next.set('to', to);
        next.set('groupBy', groupBy);
        next.set(key, value);
        /* An empty pharmacy is absent from the URL rather than `siteId=`,
           so a shared link reads as the whole contract instead of looking
           like a filter somebody forgot to fill in. */
        if (next.get('siteId') === '') next.delete('siteId');
        setParams(next, { replace: true });
    };

    /* A real component rather than a helper returning JSX, because it is
       called five times and it now holds a pager: a hook inside a function
       called N times per render is a hook order that changes with N. */
    const table = (title: string, first: string, slices: Slice[]) => (
        <SliceTable
            key={title}
            title={title}
            first={first}
            slices={slices}
            target={report.target.completion}
        />
    );

    return (
        <>
            <h1>Performance</h1>
            <p className="izy-sub">
                <Link to={`/projects/${code}`}>{project.name}</Link> · {report.from} to {report.to} · {report.timezone}
                {/* NAMED IN THE SUBTITLE, not only in the select. These
                    numbers get screenshotted into emails, and a 94%
                    completion rate with no counter beside it is a figure
                    about the contract to everybody who reads it later. */}
                {siteId !== '' && (
                    <> · {sites.find((st) => String(st.id) === siteId)?.name ?? `pharmacy ${siteId}`} only</>
                )}
            </p>

            {msg && <div className="izy-alert error" role="alert">{msg}</div>}

            <div className="izy-card">
                <div className="izy-row">
                    <label className="izy-field">From
                        <input type="date" value={from} onChange={(e) => set('from', e.target.value)} />
                    </label>
                    <label className="izy-field">To
                        <input type="date" value={to} onChange={(e) => set('to', e.target.value)} />
                    </label>
                    <label className="izy-field">Group by
                        <select value={groupBy} onChange={(e) => set('groupBy', e.target.value)}>
                            {GROUPINGS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                    </label>
                    <label className="izy-field">Pharmacy
                        <select value={siteId} onChange={(e) => set('siteId', e.target.value)}>
                            <option value="">Every pharmacy</option>
                            {sites.map((st) => <option key={st.id} value={String(st.id)}>{st.name}</option>)}
                        </select>
                    </label>
                    {/* A plain link: the browser downloads it and no copy of
                        the workbook is held in the tab. */}
                    <a className="izy-btn" href={`/api/projects/${code}/uh/reports/sla.xlsx?${query}`}>
                        Export to Excel
                    </a>
                </div>
            </div>

            <div className="izy-card">
                <h2>Against the contract</h2>
                <div className="izy-stats">
                    <div className={report.meetsContract === false ? 'izy-stat-bad' : undefined}>
                        <b>{pct(report.rates.completionRate)}</b>
                        <span>completion rate</span>
                    </div>
                    <div><b>{report.target.completion}%</b><span>contract requires</span></div>
                    <div><b>{report.target.internalGoal}%</b><span>internal goal</span></div>
                    <div><b>{pct(report.rates.onTimeRate)}</b><span>arrived on time</span></div>
                    <div><b>{pct(report.rates.dryRunRate)}</b><span>dry runs</span></div>
                </div>
                <p className="izy-muted">
                    {report.totals.delivered} delivered and {report.totals.notDelivered} not delivered,
                    out of {report.totals.attempts} attempted.
                    {report.totals.stillOpen > 0 && <> {report.totals.stillOpen} still open and left out of every rate.</>}
                    {report.totals.cancelled > 0 && <> {report.totals.cancelled} cancelled and excluded.</>}
                    {report.totals.notMeasured > 0 && <> {report.totals.notMeasured} could not be timed.</>}
                </p>
                {report.meetsContract === false && (
                    <div className="izy-alert warn" role="status">
                        Below the {report.target.completion} per cent the contract requires for this range.
                    </div>
                )}
            </div>

            {table('By period', report.grouping === 'day' ? 'Service date' : 'Period', report.byPeriod)}
            {table('By service type', 'Service type', report.byServiceType)}
            {table('By pharmacy', 'Pharmacy', report.bySite)}
            {table('By zone', 'Zone', report.byZone)}
            {table('By day type', 'Day type', report.byDayType)}

            {report.turnaround && (
                <Section id="reports-turnaround" title="How long deliveries took" summary={`median ${minutes(report.turnaround.inOurHands.medianMinutes)} in our hands`}>
                    {/* Median first, because it is the one that describes a
                        normal day. The 90th percentile beside it because the
                        tail is what a hospital is actually worried about, and
                        a median on its own hides it. */}
                    <table className="izy-table">
                        <thead>
                            <tr>
                                <th>Span</th><th>Median</th><th>90th percentile</th><th>Deliveries measured</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>In our hands, pickup to handover</td>
                                <td>{minutes(report.turnaround.inOurHands.medianMinutes)}</td>
                                <td>{minutes(report.turnaround.inOurHands.p90Minutes)}</td>
                                <td>{report.turnaround.inOurHands.count}</td>
                            </tr>
                            <tr>
                                <td>End to end, request to handover</td>
                                <td>{minutes(report.turnaround.endToEnd.medianMinutes)}</td>
                                <td>{minutes(report.turnaround.endToEnd.p90Minutes)}</td>
                                <td>{report.turnaround.endToEnd.count}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="izy-muted">
                        Delivered orders only. A failed attempt has no handover to measure to,
                        and counting one as nil would flatter the figure.
                    </p>
                </Section>
            )}

            {report.failureReasons && (
                <Section id="reports-failures" title="Why deliveries failed" summary={`${report.failureReasons.length} ${report.failureReasons.length === 1 ? "reason" : "reasons"}`}>
                    {report.failureReasons.length === 0 ? (
                        /* Said out loud. A blank card reads as a page that did
                           not load; this reads as a clean fortnight. */
                        <p className="izy-muted">No failed deliveries in this range.</p>
                    ) : (
                        <>
                            <table className="izy-table">
                                <thead>
                                    <tr><th>Reason</th><th>Packages</th><th>Deliveries affected</th></tr>
                                </thead>
                                <tbody>
                                    {report.failureReasons.map((r) => (
                                        <tr key={r.code}>
                                            <td>{r.label}</td>
                                            <td>{r.packages}</td>
                                            <td>{r.orders}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <p className="izy-muted">
                                Counted per package, because three items at one door can fail for
                                three reasons and a courier records each.
                            </p>
                        </>
                    )}
                </Section>
            )}

            <Section id="reports-glossary" title="What these numbers mean" summary="how each figure is counted">
                {/* On the page, not behind a link. A rate whose basis is a
                    click away is a rate somebody quotes without the basis. */}
                <table className="izy-table">
                    <thead><tr><th>Measure</th><th>Definition</th><th>Note</th></tr></thead>
                    <tbody>
                        {pagedDefs.rows.map((d) => (
                            <tr key={d.measure}>
                                <td><b>{d.measure}</b></td>
                                <td>{d.definition}</td>
                                <td className="izy-muted">{d.note}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pager of={pagedDefs} noun="measures" />
                {report.rates.literalScopeRatio !== null && (
                    <p className="izy-muted">
                        Scope 1.2.5 as literally written (attempts divided by successful deliveries) gives{' '}
                        {report.rates.literalScopeRatio.toFixed(3)} for this range, which cannot be a percentage.
                        Raise it with University Health before the first quarterly review.
                    </p>
                )}
            </Section>
        </>
    );
}

function SliceTable({ title, first, slices, target }: {
    title: string;
    first: string;
    slices: Slice[];
    target: number;
}) {
    const paged = usePaged(slices);
    return (
        <Section id={`reports-slice-${title.toLowerCase().replace(/\W+/g, "-")}`} title={title} summary={slices.length === 0 ? "nothing in this range" : `${slices.length} ${slices.length === 1 ? "row" : "rows"}`}>
            {slices.length === 0 ? <p className="izy-muted">Nothing in this range.</p> : (
                <>
                    <table className="izy-table">
                        <thead>
                            <tr>
                                <th>{first}</th><th>Deliveries</th><th>Attempted</th><th>Delivered</th>
                                <th>Not delivered</th><th>Completion</th><th>On time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.rows.map((s) => (
                                <tr key={s.key}>
                                    <td>{s.label}</td>
                                    <td>{s.totals.orders}</td>
                                    <td>{s.totals.attempts}</td>
                                    <td>{s.totals.delivered}</td>
                                    <td>{s.totals.notDelivered}</td>
                                    <td className={s.rates.completionRate !== null && s.rates.completionRate < target ? 'izy-stat-bad' : undefined}>
                                        {pct(s.rates.completionRate)}
                                    </td>
                                    <td>{pct(s.rates.onTimeRate)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pager of={paged} noun="rows" />
                </>
            )}
        </Section>
    );
}
